import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Modal,
} from "react-bootstrap";
import { FaPaperclip, FaPaperPlane, FaUpload } from "react-icons/fa";
import "./ViewChat.css";
import { socket } from "../../utils/socket";
import { useParams } from "react-router-dom";
import {
  useGetAllMessagesQuery,
  useGetImagesQuery,
  useSendMessageMutation,
} from "../../store";
import Skeleton from "react-loading-skeleton";
import LoadingBox from "../../components/layout/LoadingBox";
import UploadFileComp from "../../components/layout/UploadFileComp";
import { ApproveButton } from "../../components/layout/CustomTable";
import { useApproveMsgMutation } from "../../store/api/chats";
import { ConfirmationModal } from "../../components";
import ApproveMessage from "./ApproveMsg";

const FileUpload = ({
  showModal,
  setShowModal,
  file,
  setFile,
  handleSend,
  isSendMessageLoading,
}) => {
  return (
    <>
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setFile(null);
        }}
      >
        <Modal.Header>
          <h5>Upload File</h5>
        </Modal.Header>
        <Modal.Body
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {file && <iframe src={file.image}></iframe>}
          <UploadFileComp
            file={file}
            setFile={setFile}
            style={{ width: "100%" }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              setFile(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!file}
            onClick={() => {
              handleSend(file);
            }}
          >
            Send{" "}
            {isSendMessageLoading ? (
              <LoadingBox style={{ height: "1rem", width: "1rem" }} />
            ) : (
              <FaUpload />
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ViewChat = () => {
  const { id } = useParams();
  const { isLoading, isSuccess, isError, error, data } = useGetImagesQuery(id);
  const userInfo = JSON.parse(localStorage.getItem("user"));
  const messagesEndRef = useRef(null);
  const [file, setFile] = useState(null);
  const [sendMessage, { isLoading: isSendMessageLoading }] =
    useSendMessageMutation();
  const {
    isLoading: isMessagesLoading,
    isSuccess: isMessagesSuccess,
    isError: isMessagesError,
    error: messagesError,
    data: messagesData,
    refetch,
  } = useGetAllMessagesQuery(id);

  const [
    approveMsg, {
      isLoading: isApproveMsgLoading,
      isSuccess: isApproveMsgSuccess,
      error: approveError
    }] = useApproveMsgMutation()

  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState(messagesData?.messages || []);
  const [images, setImages] = useState({ admin: "", lender: "", appraier: "" });
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);

  const handleSend = async (file) => {
    const trimmedMessage = message.trim() || file?.image?.trim();
    if (trimmedMessage) {
      try {
        const response = await sendMessage({
          jobId: id,
          senderId: userInfo.id,
          content: trimmedMessage,
        }).unwrap();
        setMessage("");
        setFile(null);
        setShowModal(false);
        socket.emit("send_message", response);
        refetch();
      } catch (error) {
        console.error("Failed to send message:", error);
      }
    }
  };

  const openConfirmModal = (messageId) => {
    setSelectedMessageId(messageId);
    setShowConfirmationModal(true);
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    socket.on("new_message", (data) => {
      setMessages((prev) => [...prev, data.data]);
      refetch();
    });
  }, [socket]);

  useEffect(() => {
    socket.emit("join_chat", id);
    socket.on("confirm_joining", (data) => {
      console.log("joined successfully", data);
    });
    return () => {
      socket.off();
    };
  }, []);

  useEffect(() => {
    if (isSuccess && data.chat) {
      const { admin, lender, appraiser } = data?.chat;
      setImages({
        admin: admin?.image,
        lender: lender?.image,
        appraiser: appraiser?.image,
      });
    }
  }, [isSuccess, data, isApproveMsgSuccess]);

  useEffect(() => {
    if (messagesData) {
      setMessages(messagesData.messages);
    }
  }, [messagesData]);

  useEffect(() => {
    if (isApproveMsgSuccess) {
      console.log("Approve Success")
    }
  }, [isApproveMsgSuccess])

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <>
      <Container className="chat-container">
        {/* Header */}
        <Row className="chat-header mb-3 align-items-center">
          <Col xs={12} className="d-flex align-items-center">
            <div className="chat-job-info me-3">
              <h5 className="mb-0">Residential Appraisal</h5>
              <p className="mb-0" style={{ color: "#e1e1e1e8" }}>
                Brampton, Canada
              </p>
            </div>
            <div className="chat-avatars">
              {!isSuccess ? (
                <Skeleton
                  count={1}
                  circle={true}
                  height={40}
                  width={40}
                  baseColor="#afafaf"
                />
              ) : (
                Object.keys(images).map((item, i) => (
                  <img
                    src={images[item]}
                    alt={i}
                    key={i}
                    className="rounded-circle"
                  />
                ))
              )}
            </div>
          </Col>
        </Row>

        {/* Messages Section */}
        <Row className="chat-messages" style={{ display: "block" }}>
          {messages.map((msg) => (
            <Col
              key={msg.id}
              xs={12}
              className={`d-flex ${msg.senderRole === "admin"
                ? "justify-content-end"
                : "justify-content-start"
                } mb-2 align-items-center gap-2`}
            >
              <div
                className={`message-bubble ${msg.senderRole === "admin" ? "current-user" : "other-user"
                  }`}
              >
                <div className="d-flex align-items-center mb-1">
                  <img
                    src={
                      msg.senderRole === "admin"
                        ? images.admin
                        : msg.senderRole === "lender"
                          ? images.lender
                          : images.appraiser
                    }
                    alt={`${msg.sender} Avatar`}
                    className="avatar me-2"
                  />
                  <strong>{msg.sender}</strong>
                  <span className="text-muted ms-2 small">
                    {msg.senderRole}
                  </span>
                </div>
                <p className="mb-1">{msg.message}</p>
                <small className="text-muted">{msg.time}</small>
              </div>
              {msg.senderRole === "admin" ? null : (
                <ApproveButton
                  disabled={msg.approved}
                  onClick={() => openConfirmModal(msg.id)}
                >
                  Approve
                </ApproveButton>
              )}
            </Col>
          ))}
        </Row>

        <ApproveMessage
          show={showConfirmationModal}
          onHide={() => setShowConfirmationModal(false)}
          id={selectedMessageId}
        />

        {/* Input Section */}
        <Row className="chat-input mt-3">
          <Col xs={12} style={{ display: "flex" }}>
            <Button
              variant="link"
              onClick={() => setShowModal(true)}
            // disabled={!message.trim()}
            >
              <FaPaperclip />
            </Button>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSend();
                }}
              />
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={!message.trim()}
              >
                {isSendMessageLoading ? (
                  <LoadingBox style={{ height: "1.3rem", width: "1.3rem" }} />
                ) : (
                  <FaPaperPlane />
                )}
              </Button>
            </InputGroup>
          </Col>
        </Row>
      </Container>
      <FileUpload
        showModal={showModal}
        setShowModal={setShowModal}
        file={file}
        setFile={setFile}
        handleSend={handleSend}
        isSendMessageLoading={isSendMessageLoading}
      />
    </>
  );
};

export default ViewChat;
